import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function readCookie(name) {
  const parts = (document.cookie || "").split(";");
  for (const part of parts) {
    const [k, ...rest] = part.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("="));
  }
  return "";
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach the CSRF token from the double-submit cookie on every mutating request.
// (No-op if the cookie isn't set — safe endpoints don't check for the header.)
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
    const token = readCookie("csrf_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers["X-CSRF-Token"] = token;
    }
  }
  return config;
});

// Self-heal CSRF 403s. If the server rejected a mutating request because the
// cookie went stale (expired after 24h or wiped by another tab), transparently
// refresh the token via /auth/csrf and retry the original request ONCE.
// This never retries if:
//  - the error isn't a CSRF 403 (e.g. permission denied)
//  - the refresh itself fails (session is actually gone)
//  - we've already retried this specific request
let _csrfRefreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const resp = error?.response;
    const cfg = error?.config || {};
    const detail = resp?.data?.detail;
    const isCsrfFail = resp?.status === 403 && detail === "CSRF verification failed";
    if (!isCsrfFail || cfg.__csrfRetried) return Promise.reject(error);
    try {
      // Coalesce concurrent refreshes so a burst of failures hits /auth/csrf once.
      _csrfRefreshing = _csrfRefreshing || api.post("/auth/csrf");
      await _csrfRefreshing;
    } catch {
      return Promise.reject(error);
    } finally {
      _csrfRefreshing = null;
    }
    cfg.__csrfRetried = true;
    return api.request(cfg);
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export const INR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

export const compactINR = (n) => {
  if (!n) return "₹0";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${Math.round(n)}`;
};
